import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ExportJobService, ExportJobState } from '../export-job.service';

/**
 * Progress card pinned to the bottom-right of the window while an export is running.
 * Sits opposite the toasters, which stack from the top, so the two never collide.
 *
 * It lives in the app shell rather than on the dashboard because the export outlives the
 * page that started it — the user can navigate anywhere and still see it working.
 *
 * The bar is deliberately indeterminate for most of the run. The slow part is the server
 * building the response, during which no byte count exists, so a percentage there would be
 * invented. It switches to a real percentage only once bytes are arriving AND the response
 * declared a Content-Length.
 */
@Component({
  selector: 'app-export-progress-bar',
  templateUrl: './export-progress-bar.component.html',
  styleUrls: ['./export-progress-bar.component.scss']
})
export class ExportProgressBarComponent implements OnInit, OnDestroy {
  job: ExportJobState | null = null;
  elapsedLabel = '';

  private subscription?: Subscription;
  private ticker?: any;

  constructor(private _exportJobs: ExportJobService) { }

  ngOnInit() {
    // Only one export runs at a time today; showing the first keeps the bar unambiguous.
    this.subscription = this._exportJobs.jobs$.subscribe((jobs: ExportJobState[]) => {
      this.job = jobs.length ? jobs[0] : null;
      this.updateElapsed();
      this.job ? this.startTicker() : this.stopTicker();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.stopTicker();
  }

  get message(): string {
    if (!this.job) {
      return '';
    }
    switch (this.job.phase) {
      case 'waiting':
        return `Preparing ${this.job.label}`;
      case 'downloading':
        return this.job.percent === null
          ? `Downloading ${this.job.label} · ${this.formatSize(this.job.loaded)}`
          : `Downloading ${this.job.label}`;
      case 'building':
        return `Building your ${this.job.label} file`;
    }
  }

  /** Sets expectations during the long, featureless wait; hidden once bytes are flowing. */
  get hint(): string {
    return this.job?.phase === 'waiting'
      ? 'The server is still gathering the records — this can take a few minutes'
      : '';
  }

  cancel() {
    if (this.job) {
      this._exportJobs.cancel(this.job.key);
    }
  }

  /**
   * Nothing emits during the 'waiting' phase — no bytes, no events — so without a local
   * tick the bar would look frozen exactly when the user most wants proof it is alive.
   */
  private startTicker() {
    if (this.ticker) {
      return;
    }
    this.ticker = setInterval(() => this.updateElapsed(), 1000);
  }

  private stopTicker() {
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = undefined;
    }
  }

  private updateElapsed() {
    if (!this.job) {
      this.elapsedLabel = '';
      return;
    }
    const seconds = Math.max(0, Math.floor((Date.now() - this.job.startedAt) / 1000));
    if (seconds < 60) {
      this.elapsedLabel = `${seconds}s`;
      return;
    }
    const minutes = Math.floor(seconds / 60);
    this.elapsedLabel = `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`;
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
